import React from 'react';

const QuantumLinkAnimation = ({ p5, x, y, width, height }) => {
  // Quantum particle effect parameters
  const pairCount = 5; // Match harmonic count for symmetry
  const time = p5.frameCount * 0.003; // Even slower for more graceful motion
  const centerX = x + width/2;
  const centerY = y + height/2;

  // Resonant frequencies based on golden ratio harmonics
  const baseFreq = 0.3;
  // Golden ratio-based harmonics for more natural resonance
  const harmonics = [1, 1.618, 2.618, 4.236, 6.854];

  // Draw quantum energy pulses showing resonance nodes
  const pulseCount = 5; // Match harmonic count
  for (let i = 0; i < pulseCount; i++) {
    const pulseFreq = baseFreq * harmonics[i];
    const pulseProgress = ((time * pulseFreq) % 1);
    const pulseRadius = 30 + pulseProgress * 200;
    const resonanceEffect = Math.sin(time * pulseFreq * Math.PI * 0.5);
    const pulseAlpha = 30 * Math.pow(1 - pulseProgress, 2) * (0.8 + Math.abs(resonanceEffect) * 0.2);
    
    p5.stroke(0, 255, 0, pulseAlpha);
    p5.strokeWeight(1 + Math.abs(resonanceEffect));
    p5.noFill();
    p5.circle(centerX, centerY, pulseRadius * 2);
  }

  // Draw resonant particle pairs
  for (let i = 0; i < pairCount; i++) {
    const harmonic = harmonics[i % harmonics.length];
    const baseAngle = (i / pairCount) * Math.PI * 2;
    
    // Calculate golden spiral orbital parameters
    const orbitFreq = baseFreq * harmonic;
    const phaseAngle = time * orbitFreq;
    
    // Create complex resonance pattern using multiple harmonics
    let resonancePhase = 0;
    for (let h = 0; h < harmonics.length; h++) {
      resonancePhase += Math.sin(phaseAngle * harmonics[h]) * Math.cos(time * baseFreq * harmonics[h]) / harmonics.length;
    }
    
    // Base radius follows golden spiral
    const spiralGrowth = 1.618033988749895; // Golden ratio
    const radiusBase = 40 * Math.pow(spiralGrowth, i % 3);
    const standingWave = Math.sin(harmonic * baseAngle) * Math.sin(phaseAngle * 1.618);
    
    // Calculate positions for resonant pair with spiral influence
    for (let j = 0; j < 2; j++) {
      const angle = baseAngle + phaseAngle + (j * Math.PI) + resonancePhase * 0.5;
      const radius = radiusBase + standingWave * (10 + i * 5);
      
      const px = centerX + Math.cos(angle) * radius;
      const py = centerY + Math.sin(angle) * radius;
      
      // Calculate resonance intensity
      const resonanceIntensity = 0.5 + Math.abs(standingWave) * 0.5;
      const particleGlow = 35 + resonanceIntensity * 25;
      
      // Resonant field visualization
      const fieldSize = 8 + resonanceIntensity * 8;
      
      // Outer resonance field
      p5.fill(0, 255, 0, particleGlow * 0.2);
      p5.noStroke();
      p5.circle(px, py, fieldSize * 3);
      
      // Standing wave pattern
      p5.fill(0, 255, 0, particleGlow * 0.4);
      p5.circle(px, py, fieldSize * 2);
      
      // Core particle with resonance glow
      p5.fill(0, 255, 0, particleGlow);
      p5.circle(px, py, fieldSize);
      
      // Draw resonant entanglement patterns
      if (j === 0) {
        const pairAngle = angle + Math.PI;
        const pair_px = centerX + Math.cos(pairAngle) * radius;
        const pair_py = centerY + Math.sin(pairAngle) * radius;
        
        // Calculate interference pattern
        const interference = Math.cos(harmonic * angle) * Math.sin(phaseAngle);
        const patternIntensity = 0.3 + Math.abs(interference) * 0.7;
        
        // Create fibonacci-based wave patterns between pairs
        const waveCount = Math.floor(harmonic);
        for (let k = 0; k < waveCount; k++) {
          const phi = 1.618033988749895; // Golden ratio
          const wavePhase = (k / waveCount) * Math.PI * phi + time * harmonic;
          const waveAmplitude = Math.sin(wavePhase) * (20 + k * 8) * patternIntensity;
          
          // Calculate spiral control points
          const t = k / (waveCount - 1);
          const spiralRadius = 20 * Math.pow(phi, k % 3);
          const spiralAngle = 2 * Math.PI * phi * t + time;
          
          const midX = (px + pair_px) / 2;
          const midY = (py + pair_py) / 2;
          const perpX = -(pair_py - py);
          const perpY = pair_px - px;
          const perpLength = Math.sqrt(perpX * perpX + perpY * perpY);
          const normX = perpX / perpLength;
          const normY = perpY / perpLength;
          
          // Spiral-influenced control points
          const ctrl1X = midX + normX * waveAmplitude + Math.cos(spiralAngle) * spiralRadius;
          const ctrl1Y = midY + normY * waveAmplitude + Math.sin(spiralAngle) * spiralRadius;
          const ctrl2X = midX - normX * waveAmplitude + Math.cos(spiralAngle + Math.PI) * spiralRadius;
          const ctrl2Y = midY - normY * waveAmplitude + Math.sin(spiralAngle + Math.PI) * spiralRadius;
          
          // Draw resonant wave pattern with varying intensity
          const waveIntensity = Math.pow(Math.sin(wavePhase), 2) * patternIntensity;
          p5.stroke(0, 255, 0, 20 * waveIntensity);
          p5.noFill();
          p5.strokeWeight(1 + waveIntensity);
          p5.bezier(px, py, ctrl1X, ctrl1Y, ctrl2X, ctrl2Y, pair_px, pair_py);
        }
        
        // Core resonance beam
        p5.stroke(0, 255, 0, 40 * resonanceIntensity);
        p5.strokeWeight(1);
        p5.line(px, py, pair_px, pair_py);
      }
    }
  }
  // Draw resonant interference scanning lines
  const scanCount = 3;
  for (let i = 0; i < scanCount; i++) {
    const scanPhase = (time * baseFreq * (1 + i * 0.5)) % 1;
    const scanY = y + height * scanPhase;
    
    // Create standing wave pattern across scan line
    for (let w = 0; w < width; w += 10) {
      const xPhase = (w / width) * Math.PI * 4;
      let resonancePattern = 0;
      
      // Combine multiple harmonics for complex interference pattern
      for (let h = 0; h < harmonics.length; h++) {
        resonancePattern += Math.sin(xPhase * harmonics[h] + time * harmonics[h]) / harmonics.length;
      }
      
      const waveY = scanY + resonancePattern * 15;
      const nextX = Math.min(x + w + 10, x + width);
      const nextPhase = ((w + 10) / width) * Math.PI * 4;
      let nextResonance = 0;
      
      // Combine multiple harmonics for next point
      for (let h = 0; h < harmonics.length; h++) {
        nextResonance += Math.sin(nextPhase * harmonics[h] + time * harmonics[h]) / harmonics.length;
      }
      
      const nextY = scanY + nextResonance * 15;
      const intensity = 0.3 + Math.abs(resonancePattern) * 0.7;
      const baseAlpha = 12 - i * 3;
      
      // Draw layered resonance pattern
      p5.stroke(0, 255, 0, baseAlpha * 0.3 * intensity);
      p5.strokeWeight(4);
      p5.line(x + w, waveY, nextX, nextY);
      
      p5.stroke(0, 255, 0, baseAlpha * 0.6 * intensity);
      p5.strokeWeight(2);
      p5.line(x + w, waveY, nextX, nextY);
      
      p5.stroke(0, 255, 0, baseAlpha * intensity);
      p5.strokeWeight(1);
      p5.line(x + w, waveY, nextX, nextY);
    }
  }
};

export default QuantumLinkAnimation;